import { ErrorHandler, Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import type { PortalCategory } from '../category/portal-category';
import type { BrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import { fromMasked, toMasked } from '../shared/masked-ean';
import { type BrandCatalogProduct, CatalogRepository } from './catalog-repository';
import type { ProductRecord } from './product-record';
import { PRODUCT_RECORD_COLUMNS, rowToProductRecord } from './product-record-row';

const CATEGORY_TABLE = 'portal_categories';
const CATEGORY_COLUMNS =
  'slug, display_name, parent_slug, is_purchasable, revenue_magnitude, sku_cap, is_active';
const CATEGORY_PAGE_SIZE = 100;

const PRODUCT_TABLE = 'medpdb2';

// The two columns the brand catalogue answers with.
const PRODUCT_COLUMNS = 'EAN, category, name, page';

// PostgREST's max_rows, so a full page means "ask again" and a short one means "that was all".
const PRODUCT_PAGE_SIZE = 1000;

// Keys per `.in()` filter: the URL is a GET, so the whole key list rides in the query string
// (the brand and availability ACLs split theirs the same way).
const KEY_BATCH_SIZE = 200;

// Batches in flight at once, so a large comparison is not read one round trip at a time.
const MAX_CONCURRENT_BATCHES = 5;

// biome-ignore lint/suspicious/noExplicitAny: postgrest-js builders are typed against the generated schema, which the portal reads without
type QueryBuilder = any;

@Injectable()
export class SupabaseCatalogRepository extends CatalogRepository {
  // Not AuthStore: that import is portal-domain-no-portal-outside-domain.
  private readonly supabase = inject(SupabaseService);
  private readonly errorHandler = inject(ErrorHandler);

  async listCategories(): Promise<readonly PortalCategory[]> {
    const categories: PortalCategory[] = [];
    let offset = 0;

    for (;;) {
      const { data, error } = await this.supabase.client
        .from(CATEGORY_TABLE)
        .select(CATEGORY_COLUMNS)
        .eq('is_active', true)
        .order('display_name')
        .range(offset, offset + CATEGORY_PAGE_SIZE - 1);

      if (error) {
        throw new Error(`[CatalogRepository] read of ${CATEGORY_TABLE} failed: ${error.message}`);
      }

      const page = (data ?? []) as readonly PortalCategory[];
      categories.push(...page);
      if (page.length < CATEGORY_PAGE_SIZE) return categories;
      offset += CATEGORY_PAGE_SIZE;
    }
  }

  /**
   * Bound to `brand_slug` and nothing else (PRD D-02): `medpdb2.brand` holds scraped display
   * names, and slug input against it returned wrong or empty results for ~38% of the catalogue
   * (.claude/rules/data-access.md). Where the ingest's slug dialect diverges from the brand
   * vocabulary the portal is given, that is a data gap, and this read does not paper over it
   * with a union or a display-alias round trip.
   */
  async productsOfBrand(brand: BrandSlug): Promise<readonly BrandCatalogProduct[]> {
    const products: BrandCatalogProduct[] = [];
    let unstoredKeys = 0;
    let offset = 0;

    for (;;) {
      const { data, error } = await this.supabase.client
        .from(PRODUCT_TABLE)
        .select(PRODUCT_COLUMNS)
        .eq('brand_slug', brand)
        // A total order over a unique column: `.range()` over an unordered relation is not a
        // partition and silently drops rows (segment-evaluator.service.ts).
        .order('EAN', { ascending: true })
        .range(offset, offset + PRODUCT_PAGE_SIZE - 1);

      if (error) {
        throw new Error(`[CatalogRepository] read of ${PRODUCT_TABLE} failed: ${error.message}`);
      }

      // Members stay `unknown` and are narrowed by the mapper
      // (.claude/rules/data-access.md, "Reading rows: validate once").
      const page = (data ?? []) as readonly CatalogueRow[];
      for (const row of page) {
        const product = rowToBrandCatalogProduct(row);
        if (product) products.push(product);
        else unstoredKeys++;
      }

      if (page.length < PRODUCT_PAGE_SIZE) break;
      offset += PRODUCT_PAGE_SIZE;
    }

    this.reportUnstoredKeys('productsOfBrand', unstoredKeys);
    return products;
  }

  async productRecords(eans: readonly EAN[]): Promise<readonly ProductRecord[]> {
    if (eans.length === 0) return [];
    const batches = chunk(eans.map(toMasked), KEY_BATCH_SIZE);
    const rows: Record<string, unknown>[] = [];
    for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
      const wave = await Promise.all(
        batches
          .slice(i, i + MAX_CONCURRENT_BATCHES)
          .map((batch) =>
            this.readAllPages(() =>
              this.supabase.client
                .from(PRODUCT_TABLE)
                .select(PRODUCT_RECORD_COLUMNS)
                .in('EAN', batch)
            )
          )
      );
      for (const page of wave) rows.push(...page);
    }
    return this.recordsOf('productRecords', rows);
  }

  // Every row the filtered query holds, page by page. A total order over a unique column:
  // `.range()` over an unordered relation is not a partition and silently drops rows.
  private async readAllPages(
    query: () => QueryBuilder
  ): Promise<readonly Record<string, unknown>[]> {
    const rows: Record<string, unknown>[] = [];
    let offset = 0;
    for (;;) {
      const { data, error } = await query()
        .order('EAN', { ascending: true })
        .range(offset, offset + PRODUCT_PAGE_SIZE - 1);
      if (error) {
        throw new Error(`[CatalogRepository] read of ${PRODUCT_TABLE} failed: ${error.message}`);
      }
      const page = (data ?? []) as readonly Record<string, unknown>[];
      rows.push(...page);
      if (page.length < PRODUCT_PAGE_SIZE) return rows;
      offset += PRODUCT_PAGE_SIZE;
    }
  }

  // Members stay `unknown` and are narrowed by the mapper; a key that is not a stored EAN
  // is counted and reported once for the whole read.
  private recordsOf(
    method: string,
    rows: readonly Record<string, unknown>[]
  ): readonly ProductRecord[] {
    const records: ProductRecord[] = [];
    let unstoredKeys = 0;
    for (const row of rows) {
      const record = rowToProductRecord(row);
      if (record) records.push(record);
      else unstoredKeys++;
    }
    this.reportUnstoredKeys(method, unstoredKeys);
    return records;
  }

  // One report per read, never per row (.claude/rules/data-access.md, "never per item").
  private reportUnstoredKeys(method: string, count: number): void {
    if (count === 0) return;
    this.errorHandler.handleError(
      new Error(
        `[CatalogRepository] ${method}: ${count} ${PRODUCT_TABLE} row(s) carry a product key that is not a masked EAN; skipped`
      )
    );
  }
}

// One `medpdb2` row, as far as PostgREST guarantees its shape: an object, members unknown.
type CatalogueRow = {
  readonly EAN?: unknown;
  readonly category?: unknown;
  readonly name?: unknown;
  readonly page?: unknown;
};

// One catalogue row read as a product, or null when its key is not a stored EAN, which the
// caller counts. An unmarked key is a contract violation, never a code that is already real.
function rowToBrandCatalogProduct(row: CatalogueRow): BrandCatalogProduct | null {
  const ean = typeof row.EAN === 'string' ? fromMasked(row.EAN) : null;
  if (!ean) return null;
  return { ean, category: asText(row.category), name: asText(row.name), page: asText(row.page) };
}

// The category path the row carries, or null when the column is empty or is not text.
function asText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function chunk<T>(values: readonly T[], size: number): readonly (readonly T[])[] {
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
}
